<template>
	<transition name="DialogFade">
		<div
			v-if="open"
			:id="id"
			class="Dialog"
			:class="classes"
			@click.self="closeFromBackdrop"
			@keyup.escape="close"
			@keyup.tab="handleTab"
		>
			<div tabindex="0" ref="focusTrapFirst" class="is-visuallyHidden"></div>
			<div class="Dialog-container" role="dialog" ref="dialog" tabindex="-1" aria-labelledby="dialog-heading">
				<div class="Dialog-container-close">
					<button @click.prevent="close" ref="close" type="button">
						<CloIcon symbol="close" />
						<span class="is-visuallyHidden">{{ $t('generic.Close') }}</span>
					</button>
				</div>

				<div id="dialog-heading" class="Dialog-container-header">
					<!-- @slot An &lt;h1&gt; heading should be here -->
					<slot name="header" />
				</div>

				<div class="Dialog-container-body" tabindex="0">
					<!-- @slot The dialog's content. This is focusable because it is scrollable. -->
					<slot name="body" />
				</div>

				<div class="Dialog-container-footer">
					<!-- @slot A place for action buttons -->
					<slot name="footer" />
				</div>
			</div>
			<div tabindex="0" ref="focusTrapLast" class="is-visuallyHidden"></div>
		</div>
	</transition>
</template>

<script>
import CloIcon from '@/icons/CloIcon/CloIcon.vue';

export default {
	components: { CloIcon },
	name: 'CloDialog',
	props: {
		/**
		 * A unique ID for the element
		 */
		id: {
			type: String,
			required: false,
			default: 'dialog',
		},
		/**
		 * Fullscreen
		 */
		full: {
			type: Boolean,
			default: false,
		},
		/**
		 * Prevent closing when clicking on backdrop
		 */
		preventCloseOnBackdropClick: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			open: false,
		};
	},
	computed: {
		classes() {
			return {
				'Dialog--full': this.full,
			};
		},
	},
	mounted() {
		this.open = true;
		this.$nextTick(() => {
			this.$refs.dialog.focus();
		});
	},
	methods: {
		close() {
			this.$emit('close');
		},
		closeFromBackdrop() {
			if (!this.preventCloseOnBackdropClick) {
				this.close();
			}
		},
		handleTab(e) {
			// trap focus inside role="dialog"
			if (!e.shiftKey) {
				// if we are on the last focusable element, focus on the dialog
				if (e.target === this.$refs.focusTrapLast) {
					this.$refs.dialog.focus();
				} else {
					// prevent keeping focus on focusTrap, focus on next element (close)
					if (e.target === this.$refs.focusTrapFirst) {
						this.$refs.close.focus();
					}
				}
			} else {
				// if we are on the first focusable element, focus on the last focusable element
				// but we don't know what that is, so focus on close button instead?
				if (e.target === this.$refs.focusTrapFirst) {
					this.$refs.close.focus();
				}
			}
		},
	},
};
</script>

<style lang="scss">
// wrapper to hide screen underneath
.Dialog {
	display: flex;
	z-index: 1000;
	position: fixed;
	width: 100vw;
	height: 100vh; /* Fallback for browsers that do not support Custom Properties */
	height: calc(var(--vh, 1vh) * 100);
	top: 0;
	left: 0;
	[dir='rtl'] & {
		left: auto;
		right: 0;
	}
	background-color: transparentize($color-primary-5, 0.2);
	align-items: center;
	justify-content: center;
}

.Dialog-container {
	position: relative;
	background-color: $color-white;
	padding: $space-md;
	margin: $space-md;
	border-radius: 3px;
	box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.5);
	width: 100%;
	max-width: map-get($breakpoints, medium);
	max-height: calc(100% - #{$space-lg});
	display: flex;
	flex-direction: column;
	//outline: 0;
	@include breakpoint(medium) {
		padding: $space-lg;
	}
}

.Dialog-container-close {
	position: absolute;
	top: 0;
	right: 0;
	[dir='rtl'] & {
		left: 0;
		right: auto;
	}
	button {
		padding: $space-md;
	}
	svg {
		color: $color-neutral-3;
	}
}

.Dialog-container-header {
	flex: 0 0 auto;
	margin-right: $space-lg;
	[dir='rtl'] & {
		margin-right: 0;
		margin-left: $space-lg;
	}
}

.Dialog-container-body {
	flex: 1 1 auto;
	overflow-y: auto;
}

.Dialog-container-footer {
	flex: 0 0 auto;
	display: flex;
	gap: $space-md;
	.Button {
		margin-bottom: 0;
	}
}

// styles for Vue <transition>
.DialogFade-enter,
.DialogFade-leave-active {
	opacity: 0;
}

.DialogFade-enter-active,
.DialogFade-leave-active {
	transition: opacity 0.25s ease;
}

.Dialog--full {
	.Dialog-container {
		margin: 0;
		padding: 0;
		border-radius: 0;
		max-width: map-get($breakpoints, xxlarge);
		height: 100%;
		max-height: 100%;
	}
	.Dialog-container-header {
		@include inset-md;
	}
}
</style>
